import { Caption, css, Heading, Icon, Paragraph, styled } from 'canoe';
import React from 'react';
import { Card } from 'styleguide';

import CanduLogo from '!!react-svg-loader!canoe/assets/CanduName.svg';
import { DocumentPreview } from '@candu-ai/document/preview';
import { useAuthentication } from 'commons/hooks';

import { TemplateMetadata } from './types';

const Container = styled(Card)`
  ${({ theme }) => css`
    background-color: ${theme.colors.GlitterGrayL20};
    display: flex;
    flex-direction: column;
    margin-bottom: ${theme.spaces.s};
    margin-left: 0;
    margin-right: ${theme.spaces.s};
    width: 308px;
    overflow: hidden;
    padding: 0;
    position: relative;
    box-shadow: none;

    &:hover {
      box-shadow: ${theme.boxShadow.outline(theme.colors.GlitterGrayD10)};
    }
  `}
`;

const ImageWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex: 1;
    padding: ${theme.spaces.xs};
    position: relative;

    > img {
      display: block;
      margin: auto;
      max-height: 100%;
      max-width: 100%;
    }

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 1;
    }
  `}
`;

const NewIndicator = styled.span`
  ${({ theme }) => css`
    font-size: ${theme.fonts.body.fontSize};
    line-height: 1;
    padding: 6px;
    background: ${theme.linearGradients.AquaTeal};
    border-radius: ${theme.borderRadius.small};
    color: ${theme.colors.White};
    position: absolute;
    top: ${theme.spaces.s};
    right: ${theme.spaces.s};
  `}
`;

const TextWrapper = styled.div`
  ${({ theme }) => css`
    min-height: 98px;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: center;
    padding: 0 ${theme.spaces.xs} ${theme.spaces.s};
  `}
`;

const TemplateName = styled(Heading)`
  ${({ theme }) => css`
    color: ${theme.colors.OnyxGrayD10};
    font-weight: ${theme.fontWeights.bold};
    line-height: normal;
    margin-bottom: 0;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const TemplateDescription = styled(Paragraph)`
  ${({ theme }) => css`
    width: 100%;
    margin: 0;
    font-size: 14px;
    line-height: normal;
    color: ${theme.colors.OnyxGrayL20};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const Transform = styled.div`
  height: 670px;
  left: 0;
  position: absolute;
  width: 1240px;
  top: 0;
  transform: scale(0.23) translate(-166%, -166%);
`;

const TemplateContainer = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.White};
    height: 160px;
    position: relative;
    width: 292px;
    z-index: 1;
  `}
`;

const StyledImageBackground = styled.div`
  ${({ theme }) => css`
    width: 292px;
    height: 160px;
    background-color: ${theme.colors.White};
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    flex: 1;
  `}
`;

const OrganizationIconWrapper = styled.div`
  ${({ theme }) => css`
    height: 24px;
    padding: 2px 11px 2px 2px;
    margin-bottom: ${theme.spaces.xxs};
    background: ${theme.colors.White};
    border-radius: 18px;
    align-self: flex-start;
    display: flex;
    align-items: center;
  `}
`;

const CanduIcon = styled(Icon)`
  &,
  & svg {
    width: 58px;
    height: 20px;
  }
`;

interface Props {
  template: TemplateMetadata;
  onClick: (id: number) => void;
  isNew?: boolean;
}

export const TemplateCard = ({ template, onClick, isNew = false }: Props) => {
  const { applicationId }: any = useAuthentication();

  return (
    <Container
      key={`${template.id}${template.name}`}
      isHoverable
      onClick={() => onClick(template.id)}
    >
      <ImageWrapper>
        {template.image ? (
          <StyledImageBackground style={{ backgroundImage: `url(${template.image})` }} />
        ) : (
          <TemplateContainer>
            <Transform>
              <DocumentPreview
                showEmptyDocumentFallback
                applicationId={applicationId}
                documentId={template.id}
                isTemplate
              />
            </Transform>
          </TemplateContainer>
        )}
        {isNew && <NewIndicator>New!</NewIndicator>}
      </ImageWrapper>
      <TextWrapper>
        {template.scope === 'Global' && (
          <OrganizationIconWrapper>
            {template.creatorOrganizationPicture || template.creatorOrganizationName ? (
              <>
                {template.creatorOrganizationPicture && (
                  <img
                    alt="author's organization"
                    src={template.creatorOrganizationPicture}
                    css={{
                      height: '20px',
                      marginLeft: '3px',
                      verticalAlign: 'sub',
                    }}
                  />
                )}
                &nbsp;
                <Caption element="span">{template.creatorOrganizationName}</Caption>
              </>
            ) : (
              <CanduIcon size="xl" svg={CanduLogo} />
            )}
          </OrganizationIconWrapper>
        )}
        <TemplateName element="h4" size="XS">
          {template.name}
        </TemplateName>
        {template.description && (
          <TemplateDescription size="short">{template.description}</TemplateDescription>
        )}
      </TextWrapper>
    </Container>
  );
};
